import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Header,
  Dimmer,
  Loader,
  Table,
  Modal,
  Segment,
  Button,
  Form,
  Radio,
  TextArea,
  Icon
} from "semantic-ui-react";
import Moment from "react-moment";
import NumberFormat from "react-number-format";

import useUserData from "../../state-manipulation/hooks/useUserData";
import usePageData from "../../state-manipulation/hooks/usePageData";
import { setSelectedPage } from "../../state-manipulation/reducers/page-data/types";
import PageHeader from "../common/PageHeader";
import AxiosWrapper from "../../utils/AxiosWrapper";
import PaymentForm from "../forms/PaymentForm";
import IsAuthorized from "../common/IsAuthorized";

/* ======================
   Helpers / constants
====================== */

const emptyPaymentForm = {
  checkDate: "",
  checkNumber: "",
  amount: "",
  vendorId: -1,
  purchaseAccountCode: "",
  memo: "",
  isACH: false,
  dontResetVendor: false
};

const toMMDDYYFromYYYYMMDD = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-");
  return `${m}/${d}/${y.slice(2)}`;
};

const findVendorByPayee = (payee, vendors) => {
  if (!payee || !vendors?.length) return null;
  return vendors.find(
    (v) => v.name?.toLowerCase() === payee.toLowerCase()
  );
};

/* ======================
   Component
====================== */

export default function ManagePayments({ className }) {
  const [userData] = useUserData();
  const [, pageDispatch] = usePageData();

  const [isLoading, setIsLoading] = useState(false);
  const [paymentList, setPaymentList] = useState([]);
  const [payment, setPayment] = useState(null);

  /* ---------- VOID ---------- */
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidReasonOther, setVoidReasonOther] = useState("");
  const [isVoidFormValid, setIsVoidFormValid] = useState(false);
  const reasonRef = useRef(null);

  /* ---------- UNVOID ---------- */
  const [isUnvoidModalOpen, setIsUnvoidModalOpen] = useState(false);

  /* ---------- RESEND ---------- */
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);

  /* ---------- LISTS ---------- */
  const [vendorsList, setVendorsList] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);

  const updatePaymentForm = (patch) =>
    setPaymentForm((prev) => ({ ...prev, ...patch }));

  /* ======================
     API loaders
  ======================= */

  const getChecks = async (company) => {
    setIsLoading(true);
    const res = await AxiosWrapper({
      method: "GET",
      url: `${process.env.API_URL}/${company}/checks`,
      headers: { Authorization: `Basic ${userData.token}` }
    });
    setPaymentList(res.data.items);
    setIsLoading(false);
  };

  const getVendors = async (company) => {
    const res = await AxiosWrapper({
      method: "GET",
      url: `${process.env.API_URL}/${company}/vendors`,
      headers: { Authorization: `Basic ${userData.token}` }
    });

    setVendorsList(
      res.data.map((v) => ({
        text: v.Name,
        key: v.ContactID,
        value: v.ContactID,
        name: v.Name,
        purchasesdefaultaccountcode: v.PurchasesDefaultAccountCode
      }))
    );
  };

  const getPurchaseAccounts = async (company) => {
    const res = await AxiosWrapper({
      method: "GET",
      url: `${process.env.API_URL}/${company}/purchase-accounts`,
      headers: { Authorization: `Basic ${userData.token}` }
    });

    setPurchaseAccounts(
      res.data.map((v) => ({
        text: v.Name,
        key: v.Code,
        value: v.Code,
        code: v.Code
      }))
    );
  };

  /* ======================
     Effects
  ======================= */

  useEffect(() => {
    pageDispatch({
      type: setSelectedPage,
      payload: { selectedPage: "Payments" }
    });
  }, []);

  useEffect(() => {
    if (!userData.selectedOrg) return;
    const company = userData.selectedOrg.name.toLowerCase();
    getChecks(company);
    getVendors(company);
    getPurchaseAccounts(company);
  }, [userData.selectedOrg]);

  useEffect(() => {
    if (voidReason === "Other") {
      reasonRef.current?.focus();
      setIsVoidFormValid(voidReasonOther.trim() !== "");
    } else {
      setIsVoidFormValid(!!voidReason);
    }
  }, [voidReason, voidReasonOther]);

  /* ======================
     VOID handlers
  ======================= */

  const handleVoidModalOpen = (e, { currentpayment }) => {
    setPayment(currentpayment);
    setVoidReason("");
    setVoidReasonOther("");
    setIsVoidModalOpen(true);
  };

  const handleVoidModalClose = () => {
    setPayment(null);
    setIsVoidModalOpen(false);
  };

  const handleVoidPayment = async () => {
    await AxiosWrapper({
      method: "PATCH",
      url: `${process.env.API_URL}/${userData.selectedOrg.name.toLowerCase()}/payments/${payment.id}?action=void`,
      headers: { Authorization: `Basic ${userData.token}` },
      data: {
        voidReason,
        voidReasonOther,
        payment
      }
    });

    handleVoidModalClose();
    getChecks(userData.selectedOrg.name.toLowerCase());
  };

  /* ======================
     UNVOID handlers
  ======================= */

  const handleUnvoidModalOpen = (e, { currentpayment }) => {
    setPayment(currentpayment);
    setIsUnvoidModalOpen(true);
  };

  const handleUnvoidPayment = async () => {
    await AxiosWrapper({
      method: "PATCH",
      url: `${process.env.API_URL}/${userData.selectedOrg.name.toLowerCase()}/payments/${payment.id}?action=unvoid`,
      headers: { Authorization: `Basic ${userData.token}` },
      data: { unvoid: true }
    });

    setIsUnvoidModalOpen(false);
    setPayment(null);
    getChecks(userData.selectedOrg.name.toLowerCase());
  };

  /* ======================
     RESEND handlers
  ======================= */

  const injectEphemeralVendor = (payee) => {
    setVendorsList((prev) => {
      // Remove any previous synthetic vendor
      const withoutSynthetic = prev.filter(v => v.value !== -1);

      // Add fresh synthetic vendor for THIS payment
      return [
        {
          text: payee,
          key: -1,
          value: -1,
          name: payee
        },
        ...withoutSynthetic
      ];
    });
  };


  const handleResendModalOpen = (e, { currentpayment }) => {
    setPayment(currentpayment);

    const matchedVendor = findVendorByPayee(
      currentpayment.payee,
      vendorsList
    );

    if (!matchedVendor) {
      injectEphemeralVendor(currentpayment.payee);
    }

    updatePaymentForm({
      checkDate: toMMDDYYFromYYYYMMDD(currentpayment.checkDate),
      checkNumber:
        currentpayment.checkNumber === -1
          ? ""
          : String(currentpayment.checkNumber),
      amount: String(currentpayment.amount ?? ""),
      memo: currentpayment.memo ?? "",
      isACH: !!currentpayment.isACH,
      dontResetVendor: false,

      vendorId: matchedVendor ? matchedVendor.value : -1,

      purchaseAccountCode:
        currentpayment.purchaseAccountCode ||
        matchedVendor?.purchasesdefaultaccountcode ||
        ""
    });

    setIsResendModalOpen(true);
  };



  const handleResendModalClose = () => {
    setIsResendModalOpen(false)
  }

  const handleResendSubmit = async () => {
    if (!payment) return;

    // Resolve vendorName
    const selectedVendor =
      vendorsList.find(v => v.value === paymentForm.vendorId);

    const vendorName =
      paymentForm.vendorId === -1
        ? payment.payee
        : selectedVendor?.name || payment.payee;

    // 🔑 Resolve purchase account NAME from CODE
    const selectedPurchaseAccount =
      purchaseAccounts.find(
        a => a.value === paymentForm.purchaseAccountCode
      );

    const purchaseAccount =
      selectedPurchaseAccount?.name ||
      payment.purchaseAccount; // fallback to original

    const payload = {
      checkDate:
        paymentForm.checkDate ||
        toMMDDYYFromYYYYMMDD(payment.checkDate),

      checkNumber: paymentForm.isACH
        ? ""
        : paymentForm.checkNumber || payment.checkNumber,

      isACH: paymentForm.isACH,
      amount: paymentForm.amount || payment.amount,

      // Vendor handling
      vendorId: paymentForm.vendorId ?? -1,
      vendorName,

      // 🔑 Purchase account handling
      purchaseAccountCode:
        paymentForm.purchaseAccountCode ||
        payment.purchaseAccountCode,

      purchaseAccount, // <-- THIS was missing

      memo: paymentForm.memo || payment.memo,

      resendToXero: 1
    };

    setIsLoading(true);
    await AxiosWrapper({
      method: "POST",
      url: `${process.env.API_URL}/${userData.selectedOrg.name.toLowerCase()}/checks`,
      headers: { Authorization: `Basic ${userData.token}` },
      data: payload
    });
    setIsLoading(false);

    handleResendModalClose();
    getChecks(userData.selectedOrg.name.toLowerCase());
  };



  /* ======================
     Render
  ======================= */

  return (
    <div className={`content-container ${className}`}>
      <PageHeader pageName="Payments" orgName={userData.selectedOrg?.name} />

      <Segment>
        <Dimmer active={isLoading}>
          <Loader />
        </Dimmer>

        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                Payment Date
              </Table.HeaderCell>
              <Table.HeaderCell>
                Payee
              </Table.HeaderCell>
              <Table.HeaderCell>
                Check #
              </Table.HeaderCell>
              <Table.HeaderCell>
                Amount
              </Table.HeaderCell>
              <Table.HeaderCell>
                Purchase Account
              </Table.HeaderCell>
              <Table.HeaderCell>
                Memo
              </Table.HeaderCell>
              <Table.HeaderCell>
                Check Issued By
              </Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paymentList.map((p, i) => (
              <Table.Row
                key={i}
                className={p.isVoided ? "voided-row" : ""}
              >
                <Table.Cell>
                  <Moment format="MM/DD/YYYY">{p.checkDate}</Moment>
                </Table.Cell>

                <Table.Cell>{p.payee}</Table.Cell>

                <Table.Cell>
                  {p.checkNumber === -1 ? "Transfer" : p.checkNumber}
                </Table.Cell>

                <Table.Cell textAlign="right">
                  $
                  <NumberFormat
                    thousandSeparator
                    value={p.amount.toFixed(2)}
                    displayType="text"
                  />
                </Table.Cell>

                <Table.Cell>{p.purchaseAccount}</Table.Cell>

                <Table.Cell>
                  {p.memo}
                  {p.isVoided && (
                    <>
                      <br />
                      <span className="voided">
                        Voided: {p.voidReason} {p.voidReasonOther}
                      </span>
                    </>
                  )}
                </Table.Cell>

                <Table.Cell>{p.personWhoIssuedCheck}</Table.Cell>

                <Table.Cell>
                  {!p.isVoided && (
                    <Button
                      currentpayment={p}
                      onClick={handleVoidModalOpen}
                    >
                      Void
                    </Button>
                  )}

                  {p.isVoided && (
                    <Button
                      currentpayment={p}
                      onClick={handleUnvoidModalOpen}
                    >
                      Un-Void
                    </Button>
                  )}

                  <Button
                    currentpayment={p}
                    onClick={handleResendModalOpen}
                  >
                    Resend
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>

        </Table>

        {/* VOID MODAL */}
        <Modal open={isVoidModalOpen} closeOnEscape onClose={handleVoidModalClose}>
          <Modal.Header>Void Payment</Modal.Header>
          <Modal.Content>
            <Form>
              {["Check did not print", "Wrong vendor", "Wrong amount", "Wrong date", "Wrong memo", "Other"].map((r) => (
                <Form.Field key={r}>
                  <Radio
                    label={r}
                    checked={voidReason === r}
                    onChange={() => setVoidReason(r)}
                  />
                </Form.Field>
              ))}
              <TextArea
                ref={reasonRef}
                disabled={voidReason !== "Other"}
                value={voidReasonOther}
                onChange={(e, { value }) => setVoidReasonOther(value)}
                placeholder="Tell us more"
              />
              <Button
                primary
                disabled={!isVoidFormValid}
                onClick={handleVoidPayment}
              >
                Save
              </Button>
              <Button secondary onClick={handleVoidModalClose}>
                Cancel
              </Button>
            </Form>
          </Modal.Content>
        </Modal>

        {/* UNVOID MODAL */}
        <Modal basic open={isUnvoidModalOpen}>
          <Header icon="archive" content="Unvoid Payment" />
          <Modal.Content>
            Are you sure you want to unvoid this payment?
          </Modal.Content>
          <Modal.Actions>
            <Button basic color="red" inverted onClick={() => setIsUnvoidModalOpen(false)}>
              <Icon name="remove" /> No
            </Button>
            <Button color="green" inverted onClick={handleUnvoidPayment}>
              <Icon name="checkmark" /> Yes
            </Button>
          </Modal.Actions>
        </Modal>

        {/* RESEND MODAL */}
        <Modal open={isResendModalOpen} closeOnEscape onClose={handleResendModalClose}>
          <Modal.Header>Resend Payment</Modal.Header>
          <Modal.Content>
            <PaymentForm
              form={paymentForm}
              vendorsList={vendorsList}
              purchaseAccounts={purchaseAccounts}
              onCheckDateChange={(e, { value }) => updatePaymentForm({ checkDate: value })}
              onCheckNumberChange={(e) => updatePaymentForm({ checkNumber: e.target.value })}
              onAmountChange={(e, { value }) => updatePaymentForm({ amount: value })}
              onVendorChange={(e, { value }) => updatePaymentForm({ vendorId: value })}
              onPurchaseAccountsChange={(e, { value }) => updatePaymentForm({ purchaseAccountCode: value })}
              onMemoChange={(e) => updatePaymentForm({ memo: e.target.value })}
              onIsACHChange={(e, { checked }) => updatePaymentForm({ isACH: checked })}
              onDontResetVendorChange={(e, { checked }) => updatePaymentForm({ dontResetVendor: checked })}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button secondary onClick={() => setIsResendModalOpen(false)}>
              Cancel
            </Button>
            <IsAuthorized permission="hasManageUsers">
              <Button primary onClick={handleResendSubmit}>
                Resend
              </Button>
            </IsAuthorized>
          </Modal.Actions>
        </Modal>
      </Segment>
    </div>
  );
}

ManagePayments.propTypes = {
  className: PropTypes.string
};

ManagePayments.defaultProps = {
  className: ""
};
